import { TypedDocumentNode, gql } from '@apollo/client';

import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';

import { useGetAuthorizationApprovals } from 'hooks/useGetAuthorizationApprovals';

import type {
  MigrateEthMutation,
  MigrateEthMutationVariables,
  PrepareMigrateEthMutation,
  PrepareMigrateEthMutationVariables,
} from './__generated__/useMigrateEth.graphql';

const PREPARE_MIGRATE_ETH_MUTATION = gql`
  mutation PrepareMigrateEthMutation($input: prepareMigrateEthInput!) {
    prepareMigrateEth(input: $input) {
      weiAmount
      authorization {
        id
        ...useGetAuthorizationApprovals_authorizationRequest
      }
    }
  }
  ${useGetAuthorizationApprovals.fragments.authorizationRequest}
` as TypedDocumentNode<
  PrepareMigrateEthMutation,
  PrepareMigrateEthMutationVariables
>;

const MIGRATE_ETH_MUTATION = gql`
  mutation MigrateEthMutation($input: migrateEthInput!) {
    migrateEth(input: $input) {
      currentUser {
        slug
      }
    }
  }
` as TypedDocumentNode<MigrateEthMutation, MigrateEthMutationVariables>;

export const useMigrateEth = () => {
  const { currentUser } = useCurrentUserContext();
  const getAuthorizationApprovals = useGetAuthorizationApprovals();
  const [prepareMigrateEth] = useMutation(PREPARE_MIGRATE_ETH_MUTATION);
  const [migrateEth] = useMutation(MIGRATE_ETH_MUTATION);

  return async () => {
    const { shouldMigrateEth } = currentUser || {};
    if (!shouldMigrateEth) return;

    const { data } = await prepareMigrateEth({ variables: { input: {} } });
    if (!data?.prepareMigrateEth) return;

    const { weiAmount, authorization } = data.prepareMigrateEth;
    if (!authorization) return;

    const { authorizationApprovals } = await getAuthorizationApprovals([
      authorization,
    ]);
    if (!authorizationApprovals) return;

    await migrateEth({
      variables: { input: { approval: authorizationApprovals[0], weiAmount } },
    });
  };
};
